import { DeleteUndo } from '../components/DeleteUndo';
import { useDeleteUndo } from '../components/useDeleteUndo';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { EventRecord } from '../../shared/contracts';
import { api } from '../api';
import { EventCard } from '../components/EventCard';
import { EditDialog, IgnoreSourceDialog } from '../components/EventDialogs';
import type { CalendarSyncStatus, EventsResponse } from '../types';
import { pickCompletionMessage } from '../../domain/encouragement';

const filters = [
  { label: '確認する', value: 'planned', description: '日時と内容を確認してください。未登録の候補は、個別に内容を確認して登録できます。' },
  { label: '残した予定', value: 'confirmed', description: '確認してカレンダーに残した予定です。日時や内容は、あとから直せます。' },
  { label: '以前まとめて保管した予定', value: 'archived', description: '未確認のまま保管した予定。カレンダーには残ります。' },
  { label: '削除した予定', value: 'deleted', description: 'カレンダーから削除した予定。元に戻せます。' },
];

const viewLabels: Record<string, string> = {
  'view:today': '今日見つけた予定',
  'view:week': '今週見つけた予定',
  'view:review': '要確認の予定',
};

export function EventsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<EventRecord[]>([]);
  const view = searchParams.get('view');
  const filter = view === 'today' || view === 'week' || view === 'review'
    ? `view:${view}`
    : searchParams.get('filter') ?? 'planned';
  const month = searchParams.get('month') ?? '';
  const year = searchParams.get('year') ?? '';
  const canSelect = filter === 'planned' || filter.startsWith('view:');
  const [calendarSync, setCalendarSync] = useState<CalendarSyncStatus | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<EventRecord | null>(null);
  const [ignoring, setIgnoring] = useState<EventRecord | null>(null);
  const [error, setError] = useState('');
  const [operationError, setOperationError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<EventRecord[] | null>(null);
  const [syncing, setSyncing] = useState(false);
  const syncRequestRef = useRef(false);
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [archiving, setArchiving] = useState<Set<string>>(new Set());
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [months, setMonths] = useState<string[]>([]);
  const [archiveYears, setArchiveYears] = useState<string[]>([]);
  const [selectedArchiveYear, setSelectedArchiveYear] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const loadRequestRef = useRef(0);
  const loadAbortRef = useRef<AbortController | null>(null);
  const deleteRequestRef = useRef(new Set<string>());
  const archiveRequestRef = useRef(new Set<string>());
  const completionMessage = useRef(pickCompletionMessage()).current;
  const load = async (nextFilter = filter, append = false, nextMonth = month, nextYear = year) => {
    const requestId = ++loadRequestRef.current;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    const params = new URLSearchParams({ limit: '30', offset: append ? String(events.length) : '0' });
    if (nextFilter.startsWith('view:')) params.set('view', nextFilter.slice('view:'.length));
    else if (nextFilter === 'deleted') params.set('deleted', 'true');
    else if (nextFilter === 'archived') params.set('archived', 'true');
    else if (nextFilter === 'confirmed') params.set('confirmed', 'true');
    if (nextMonth) params.set('month', nextMonth);
    if (nextYear) params.set('year', nextYear);
    if (append) setLoadingMore(true);
    else { setLoading(true); setLoadingMore(false); }
    setError('');
    try {
      const result = await api<EventsResponse>(`/api/events?${params}`, { signal: controller.signal });
      if (requestId !== loadRequestRef.current) return;
      setEvents((current) => append ? [...current, ...result.events] : result.events);
      setTotal(result.total); setHasMore(result.hasMore);
      setMonths(result.months);
      setArchiveYears(result.archiveYears);
      setSelectedArchiveYear(result.selectedArchiveYear ?? '');
      setCalendarSync(result.calendarSync);
      if (!append) setSelected(new Set());
    }
    catch (reason) { if (requestId === loadRequestRef.current) setError(reason instanceof Error ? reason.message : '予定を読み込めませんでした'); }
    finally { if (requestId === loadRequestRef.current) { if (append) setLoadingMore(false); else setLoading(false); } }
  };
  useEffect(() => { void load(filter, false, month, year); }, [filter, month, year]);
  useEffect(() => () => { loadRequestRef.current += 1; loadAbortRef.current?.abort(); }, []);
  const undo = useDeleteUndo(async (restored) => { if (restored) setNotice(`${restored}件の予定を元に戻しました。`); else setNotice(''); await load(); });
  const chooseFilter = (value: string) => {
    setLoading(true);
    if (value === filter && !month) { void load(value, false, ''); return; }
    setNotice('');
    setMonths([]);
    setArchiveYears([]);
    setSearchParams(value === 'planned' ? {} : { filter: value });
  };
  const chooseMonth = (value: string) => {
    setLoading(true);
    if (value === month) { void load(filter, false, value); return; }
    const params = new URLSearchParams();
    if (filter !== 'planned') params.set('filter', filter);
    if (year) params.set('year', year);
    if (value) params.set('month', value);
    setSearchParams(params);
  };
  const chooseYear = (value: string) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter !== 'planned') params.set('filter', filter);
    params.set('year', value);
    setSearchParams(params);
  };
  const toggle = (id: string) => setSelected((current) => {
    const copy = new Set(current);
    if (copy.has(id)) copy.delete(id);
    else copy.add(id);
    return copy;
  });
  const bulkDelete = async (providedIds?: string[]) => {
    const ids = providedIds ?? [...selected];
    setOperationError('');
    if (!ids.length || ids.some((id) => deleteRequestRef.current.has(id))) return;
    ids.forEach((id) => deleteRequestRef.current.add(id));
    if (!providedIds) setBusy(true);
    setDeleting((current) => new Set([...current, ...ids])); setError(''); setNotice('');
    try {
      const result = await api<{ deleted: number; successIds: string[]; failedIds: string[] }>('/api/events/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) });
      const deletedIds = new Set(result.successIds);
      undo.add(result.successIds, events.find((event) => event.id === result.successIds[0])?.title ?? '予定');
      if (result.failedIds.length) setOperationError(`${result.failedIds.length}件を削除できませんでした。成功した分は元に戻せます。`);
      setEvents((current) => current.filter((event) => !deletedIds.has(event.id)));
      setSelected(new Set());
      setTotal((current) => Math.max(0, current - result.deleted));
      setNotice(result.deleted === 1 ? '予定を削除しました。画面下の「削除した予定を見る」から戻せます。' : `${result.deleted}件を削除しました。画面下の「削除した予定を見る」から戻せます。`);
    }
    catch (reason) { setOperationError(reason instanceof Error ? reason.message : '削除できませんでした'); }
    finally {
      ids.forEach((id) => deleteRequestRef.current.delete(id));
      if (!providedIds) setBusy(false);
      setDeleting((current) => { const next = new Set(current); ids.forEach((id) => next.delete(id)); return next; });
      await load();
    }
  };
  const bulkArchive = async (providedIds?: string[]) => {
    const ids = providedIds ?? [...selected];
    if (ids.some(id => events.some(event => event.id === id && !event.calendarEventId))) {
      setError('未登録の候補は、個別に「内容を確認して登録」してください。'); return;
    }
    if (!ids.length || ids.some((id) => archiveRequestRef.current.has(id))) return;
    ids.forEach((id) => archiveRequestRef.current.add(id));
    if (!providedIds) setBusy(true);
    setArchiving((current) => new Set([...current, ...ids])); setError(''); setNotice('');
    try {
      const result = await api<{ archived: number }>('/api/events/bulk-archive', { method: 'POST', body: JSON.stringify({ ids, category: 'confirmed' }) });
      if (result.archived !== ids.length) throw new Error('一部の予定を確認して残す処理ができませんでした。画面を再読み込みして、もう一度お試しください。');
      const archivedIds = new Set(ids);
      setEvents((current) => current.filter((event) => !archivedIds.has(event.id)));
      setSelected(new Set());
      setTotal((current) => Math.max(0, current - ids.length));
      setNotice(ids.length === 1 ? '確認して残しました。Googleカレンダーには残っています。' : `${ids.length}件を確認して残しました。Googleカレンダーには残っています。`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : '確認して残す処理ができませんでした'); }
    finally {
      ids.forEach((id) => archiveRequestRef.current.delete(id));
      if (!providedIds) setBusy(false);
      setArchiving((current) => { const next = new Set(current); ids.forEach((id) => next.delete(id)); return next; });
    }
  };
  const unarchive = async (id: string) => {
    if (archiveRequestRef.current.has(id)) return;
    archiveRequestRef.current.add(id);
    setArchiving((current) => new Set([...current, id])); setError(''); setNotice('');
    try {
      await api(`/api/events/${id}/unarchive`, { method: 'POST' });
      setEvents((current) => current.filter((event) => event.id !== id));
      setTotal((current) => Math.max(0, current - 1));
      setNotice('「確認する」へ戻しました。');
    } catch (reason) { setError(reason instanceof Error ? reason.message : '一覧へ戻せませんでした'); }
    finally {
      archiveRequestRef.current.delete(id);
      setArchiving((current) => { const next = new Set(current); next.delete(id); return next; });
    }
  };
  const restore = async (id: string) => { if (busy) return; setBusy(true); setError(''); try { await api(`/api/events/${id}/restore`, { method: 'POST' }); setNotice('カレンダーに戻しました。'); await load(); } catch (reason) { setError(reason instanceof Error ? reason.message : '元に戻せませんでした。もう一度お試しください。'); } finally { setBusy(false); } };
  const ignoreSource = async (pattern: string, field: 'subject' | 'email') => {
    if (!ignoring) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await api<{ deleted: number; pattern: string }>(`/api/events/${ignoring.id}/ignore-source`, { method: 'POST', body: JSON.stringify({ pattern, field }) });
      setIgnoring(null);
      setNotice(result.deleted > 0
        ? `「${result.pattern}」を今後拾わない設定にし、今回の予定${result.deleted}件を削除しました。`
        : `「${result.pattern}」を今後拾わない設定にしました。`);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '今後拾わない設定にできませんでした');
    } finally { setBusy(false); }
  };
  const syncCalendar = async () => {
    if (syncRequestRef.current) return;
    syncRequestRef.current = true;
    setSyncing(true); setError(''); setNotice('');
    try {
      const result = await api<{ calendarSync: CalendarSyncStatus }>('/api/events/sync', {
        method: 'POST', body: JSON.stringify({ year: selectedArchiveYear || undefined }),
      });
      await load();
      setCalendarSync(result.calendarSync);
      if (result.calendarSync.state === 'synced') setNotice('カレンダーと同期しました。');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'カレンダーと同期できませんでした');
    } finally { syncRequestRef.current = false; setSyncing(false); }
  };
  return (
    <div className="page events-page">
      <section className="page-title"><h1>予定</h1><button className="button secondary" onClick={() => void syncCalendar()} disabled={syncing || busy || deleting.size > 0 || archiving.size > 0}>{syncing ? '更新中…' : 'カレンダーと同期'}</button></section>
      {(operationError || error) && <div className="alert error">{operationError || error}</div>}
      <DeleteUndo undo={undo} />
      {notice && <div className="alert success" role="status">{notice}</div>}
      {calendarSync?.state === 'permission_required' && <div className="alert warning calendar-sync-alert"><p>カレンダー側の削除・移動を反映するため、Googleの読み取り許可を追加してください。</p><a className="button primary" href="/api/auth/google/start">Googleを再接続</a></div>}
      {calendarSync?.state === 'error' && <div className="alert warning">カレンダー側の変更確認を一時的に行えませんでした。予定の表示データはそのまま保持しています。</div>}
      {viewLabels[filter] && <div className="view-summary" role="status"><strong>{viewLabels[filter]}</strong></div>}
      <section className="event-filter-panel" aria-label="予定の状態">
        <div className="filter-row primary-event-filters" role="tablist" aria-label="予定フィルター">
          {filters.slice(0, 2).map((item) => <button key={item.value} role="tab" className={filter === item.value ? 'active' : ''} aria-selected={filter === item.value} onClick={() => chooseFilter(item.value)}>{item.label}{filter === item.value && !loading && <span className="filter-count">{total}件</span>}</button>)}
        </div>
        {(filter === 'deleted' || filter === 'archived') && <h2 className="history-view-title">{filters.find((item) => item.value === filter)?.label}</h2>}
        {!filter.startsWith('view:') && <p className="event-filter-help">{filters.find((item) => item.value === filter)?.description}</p>}
      </section>
      {(filter === 'archived' || filter === 'confirmed' || filter === 'deleted') && archiveYears.length > 1 && <nav className="month-jump" aria-label="年で絞り込む" data-swipe-ignore>
        {archiveYears.map((value) => <button key={value} className={selectedArchiveYear === value ? 'active' : ''} onClick={() => chooseYear(value)}>{value}年</button>)}
      </nav>}
      {filter !== 'planned' && !filter.startsWith('view:') && months.length > 0 && <nav className="month-jump" aria-label="月で絞り込む" data-swipe-ignore>
        <button className={!month ? 'active' : ''} onClick={() => chooseMonth('')}>すべて</button>
        {months.map((value) => <button key={value} className={month === value ? 'active' : ''} onClick={() => chooseMonth(value)}>{Number(value.slice(5))}月<span>{value.slice(0, 4)}年</span></button>)}
      </nav>}
      {!loading && events.length > 0 && <div className="selection-tools">{canSelect ? <button onClick={() => setSelected(selected.size === events.length ? new Set() : new Set(events.map((event) => event.id)))}>{selected.size === events.length ? '選択解除' : 'すべて選択'}</button> : <span />}<span>{selected.size ? `${selected.size}件選択中` : `${events.length}/${total}件表示`}</span></div>}
      <div className="event-list" aria-busy={loading}>
        {loading && <div className="event-loading" role="status"><span className="progress-spinner" aria-hidden="true" /><strong>読み込み中…</strong></div>}
        {!loading && events.map((event) => <EventCard key={event.id} event={event} selectable={canSelect} selected={selected.has(event.id)} deleting={deleting.has(event.id)} archiving={archiving.has(event.id)} onSelect={toggle} onDelete={(id) => bulkDelete([id])} onArchive={(id) => bulkArchive([id])} onUnarchive={unarchive} onIgnoreSource={setIgnoring} onRestore={restore} onEdit={setEditing} />)}
        {!loading && events.length === 0 && filter === 'planned' && <div className="empty-state completion"><span>✓</span><h3>確認完了！</h3><p>{completionMessage}</p></div>}
        {!loading && events.length === 0 && filter !== 'planned' && <div className="empty-state"><span>◎</span><h3>ここにはまだありません</h3><p>別の分類も確認してみてください。</p></div>}
      </div>
      <nav className="event-history-links" aria-label="過去の予定">
        <button className={filter === 'deleted' ? 'active' : ''} aria-current={filter === 'deleted' ? 'page' : undefined} onClick={() => chooseFilter('deleted')}>削除した予定を見る</button>
        <details className="quiet-details" open={filter === 'archived' || undefined}>
          <summary>以前の保管分</summary>
          <p>未確認のまま保管した予定です。カレンダーには残っています。</p>
          <button className={filter === 'archived' ? 'active' : ''} onClick={() => chooseFilter('archived')}>以前まとめて保管した予定を見る</button>
        </details>
      </nav>
      {!loading && hasMore && <button className="button secondary load-more" disabled={loadingMore} onClick={() => void load(filter, true, month, year)}>{loadingMore ? '読み込み中…' : 'さらに30件表示'}</button>}
      {selected.size > 0 && filter !== 'deleted' && filter !== 'archived' && filter !== 'confirmed' && <div className="bulk-bar"><strong>{selected.size}件を選択</strong><div className="bulk-actions"><button className="button archive" disabled={busy} onClick={() => bulkArchive()}>{busy ? '処理中…' : '確認して残す'}</button><button className="button danger" disabled={busy} onClick={() => setDeleteConfirmation(events.filter((event) => selected.has(event.id)).map((event) => ({ ...event })))}>{busy ? '処理中…' : 'カレンダーから削除'}</button></div></div>}
      {deleteConfirmation && <div className="modal-backdrop"><section className="modal delete-confirmation" role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title"><h2 id="delete-confirm-title">{deleteConfirmation.length}件を削除しますか？</h2><p>登録済み予定は専用カレンダーから削除し、未登録候補は取り下げます。元に戻せます。</p><ul>{deleteConfirmation.map((event) => <li key={event.id}><time>{event.startDateTime.slice(0, 16).replace('T', ' ')}</time><strong>{event.title}</strong></li>)}</ul><div className="modal-actions"><button className="button secondary" onClick={() => setDeleteConfirmation(null)}>キャンセル</button><button className="button danger" onClick={() => { const ids = deleteConfirmation.map((event) => event.id); setDeleteConfirmation(null); void bulkDelete(ids); }}>この{deleteConfirmation.length}件を削除</button></div></section></div>}
      {editing && <EditDialog event={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); setNotice('日時・内容の変更を保存しました。'); await load(); }} />}
      {ignoring && <IgnoreSourceDialog event={ignoring} busy={busy} onClose={() => !busy && setIgnoring(null)} onConfirm={ignoreSource} />}
    </div>
  );
}
